'use client';

import { AgentProgress } from '@/components/ai/AgentProgress';
import type { AgentGenerationStep } from '@/lib/agent-kit/types';
import { resumeImproveStepCopy } from './step-graph';
import type { ResumeImproveStepKey, ResumeImproveTrace, ResumeImproveTraceDetail } from './types';

/**
 * The improve flow's progress checklist.
 *
 * Wraps the shared `AgentProgress` with this flow's copy, its per-step summary
 * lines, and a reasoning panel. Lives here rather than in src/components/ai so
 * the three resume flows can each own their own presentation of the shared
 * component without contending over one file.
 */

interface ResumeImproveProgressProps {
  steps: AgentGenerationStep<ResumeImproveStepKey>[];
  status: 'idle' | 'running' | 'ready' | 'error';
  error?: string | null;
  trace?: ResumeImproveTrace | null;
  className?: string;
}

const asStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * One line per completed step. Written for someone deciding whether to trust the
 * result, so the lines that matter most are the ones about what was NOT done:
 * no metrics available, nothing invented, nothing dropped.
 */
function summarize(step: AgentGenerationStep<ResumeImproveStepKey>): string | null {
  const output = step.output;
  if (!output) return null;

  switch (step.step_key) {
    case 'ASSESS_CONTENT': {
      const weaknesses = asStringList(output.weaknesses);
      return weaknesses.length > 0
        ? `Found: ${weaknesses.join(', ').replace(/_/g, ' ')}`
        : 'Nothing structurally wrong with it.';
    }
    case 'CHOOSE_APPROACH': {
      const fixes = asStringList(output.fixes).length;
      const noMetrics = output.metricPolicy === 'no_metrics_available';
      return [
        `${fixes} ${fixes === 1 ? 'fix' : 'fixes'} planned`,
        `about ${output.targetWords} words`,
        noMetrics ? 'no figures available, so none will be added' : null,
      ]
        .filter(Boolean)
        .join(' · ');
    }
    case 'REWRITE':
      return `${output.wordCount} words, from ${output.sourceWordCount}.`;
    case 'CRITIQUE': {
      const failed = asStringList(output.failedChecks);
      if (failed.length === 0) return 'Nothing invented, nothing dropped, no clichés.';
      const invented = asStringList(output.inventedNumbers);
      return invented.length > 0
        ? `Caught invented figures: ${invented.join(', ')}`
        : `Needs fixing: ${failed.join(', ')}`;
    }
    case 'POLISH':
      if (step.status === 'SKIPPED') return typeof output.note === 'string' ? output.note : null;
      return output.clean === true
        ? 'Fixed and re-checked clean.'
        : 'Fixes applied.';
    default:
      return null;
  }
}

function Detail({ detail }: { detail: ResumeImproveTraceDetail }) {
  const { plan, critique } = detail;
  return (
    <div className="space-y-3">
      {plan ? (
        <div>
          <p className="text-overline text-muted-foreground">What it decided to fix</p>
          <ul className="mt-1 space-y-0.5">
            {plan.fixes.map((fix) => (
              <li key={fix} className="text-caption text-foreground">
                {fix}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {plan && plan.materialGaps.length > 0 ? (
        <div>
          <p className="text-overline text-muted-foreground">What it did not know</p>
          <ul className="mt-1 space-y-0.5">
            {plan.materialGaps.map((gap) => (
              <li key={gap} className="text-caption text-muted-foreground">
                {gap}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-caption text-muted-foreground">
            Nothing above was filled in with a guess.
          </p>
        </div>
      ) : null}

      {critique ? (
        <div>
          <p className="text-overline text-muted-foreground">Checks</p>
          <ul className="mt-1 space-y-0.5">
            {critique.checks.map((check) => (
              <li key={check.name} className="text-caption text-muted-foreground">
                <span className={check.passed ? 'text-foreground' : 'text-destructive'}>
                  {check.passed ? 'PASS' : 'FIX'}
                </span>{' '}
                {check.name} — {check.note}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function ResumeImproveProgress({
  steps,
  status,
  error,
  trace,
  className,
}: ResumeImproveProgressProps) {
  const detail = (trace?.detail ?? null) as ResumeImproveTraceDetail | null;

  return (
    <AgentProgress<ResumeImproveStepKey>
      steps={steps}
      copy={resumeImproveStepCopy}
      status={status}
      error={error}
      doneTitle="Here is what it changed"
      summarize={summarize}
      detail={detail ? <Detail detail={detail} /> : null}
      className={className}
    />
  );
}
